import { useEffect, useState } from "react";
import { useAppSelector } from "../redux/hooks";
import { axiosClient } from "../configs/axiosClient";
import { API } from "../configs/endpoints";
import {
  FaBox,
  FaShoppingCart,
  FaUsers,
  FaNewspaper,
  FaClock,
} from "react-icons/fa";
import { MdAttachMoney } from "react-icons/md";
import type { OrderStatus } from "../types/order/order.model";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  BarChart,
  Bar,
} from "recharts";

interface DashboardStats {
  totalProducts: number;
  totalOrders: number;
  totalAdmins: number;
  totalArticles: number;
  pendingOrders: number;
  totalRevenue: number;
  recentOrders: {
    _id: string;
    orderCode: string;
    customer: { name: string };
    totalAmount: number;
    status: OrderStatus;
    createdAt: string;
  }[];
}

interface ChartData {
  monthly: { name: string; revenue: number; orders: number }[];
  status: { name: string; value: number }[];
}

const PIE_COLORS = ["#f59e0b", "#3b82f6", "#6366f1", "#10b981", "#ef4444"];

const STATUS_LABELS: Record<OrderStatus, string> = {
  pending: "Chờ xác nhận",
  confirmed: "Đã xác nhận",
  shipping: "Đang giao",
  completed: "Hoàn thành",
  cancelled: "Đã hủy",
};

const STATUS_STYLES: Record<OrderStatus, string> = {
  pending: "bg-amber-50 text-amber-600",
  confirmed: "bg-blue-50 text-blue-600",
  shipping: "bg-indigo-50 text-indigo-600",
  completed: "bg-emerald-50 text-emerald-600",
  cancelled: "bg-red-50 text-red-600",
};

export const Home = () => {
  const { admin } = useAppSelector((state) => state.admin);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [chart, setChart] = useState<ChartData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      // Tách riêng để 1 API lỗi không ảnh hưởng cái còn lại
      try {
        const res = await axiosClient.get(API.dashboard.stats);
        setStats(res.data);
      } catch (e) {
        console.error("[Dashboard] stats error:", e);
      }
      try {
        const res = await axiosClient.get(API.dashboard.chart);
        setChart(res.data);
      } catch (e) {
        console.error("[Dashboard] chart error:", e);
      }
      setLoading(false);
    };
    fetchData();
  }, []);

  const formatCurrency = (amount: number) =>
    amount.toLocaleString("vi-VN", { style: "currency", currency: "VND" });

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString("vi-VN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

  const statCards = [
    { label: "Sản phẩm", value: stats?.totalProducts ?? "—", icon: <FaBox />, gradient: "from-blue-500 to-blue-600" },
    { label: "Đơn hàng", value: stats?.totalOrders ?? "—", icon: <FaShoppingCart />, gradient: "from-emerald-500 to-emerald-600" },
    { label: "Chờ xử lý", value: stats?.pendingOrders ?? "—", icon: <FaClock />, gradient: "from-amber-500 to-orange-500" },
    { label: "Doanh thu", value: stats ? formatCurrency(stats.totalRevenue) : "—", icon: <MdAttachMoney />, gradient: "from-violet-500 to-purple-600" },
    { label: "Bài viết", value: stats?.totalArticles ?? "—", icon: <FaNewspaper />, gradient: "from-pink-500 to-rose-500" },
    { label: "Admin", value: stats?.totalAdmins ?? "—", icon: <FaUsers />, gradient: "from-cyan-500 to-teal-500" },
  ];

  return (
    <div className="p-5 space-y-6">
      {/* Greeting */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">
          Xin chào, {admin?.name}
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Tổng quan hoạt động cửa hàng hôm nay
        </p>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {statCards.map((card) => (
          <div
            key={card.label}
            className="bg-white rounded-xl border border-gray-100 p-5 flex items-center gap-4 hover:shadow-md transition-all duration-300"
          >
            <div className={`w-12 h-12 rounded-xl bg-linear-to-br ${card.gradient} flex items-center justify-center text-white text-lg shadow-lg`}>
              {card.icon}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">
                {card.label}
              </p>
              <p className="text-xl font-bold text-gray-800 mt-0.5 truncate">
                {loading ? (
                  <span className="inline-block w-16 h-6 bg-gray-100 rounded-md animate-pulse" />
                ) : (
                  card.value
                )}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Charts */}
      {chart && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Revenue Area Chart */}
          <div className="lg:col-span-2 bg-white rounded-xl border border-gray-100 p-5">
            <h2 className="text-sm font-semibold text-gray-800 mb-4">
              Doanh thu 6 tháng gần nhất
            </h2>
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={chart.monthly}>
                <defs>
                  <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#94a3b8" }} />
                <YAxis
                  tick={{ fontSize: 12, fill: "#94a3b8" }}
                  tickFormatter={(v) =>
                    v >= 1000000 ? `${(v / 1000000).toFixed(0)}M` : v >= 1000 ? `${(v / 1000).toFixed(0)}K` : v
                  }
                />
                <Tooltip
                  formatter={(value) => [formatCurrency(Number(value)), "Doanh thu"]}
                  contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 13 }}
                />
                <Area
                  type="monotone"
                  dataKey="revenue"
                  stroke="#6366f1"
                  strokeWidth={2.5}
                  fillOpacity={1}
                  fill="url(#colorRevenue)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Order Status Pie Chart */}
          <div className="bg-white rounded-xl border border-gray-100 p-5">
            <h2 className="text-sm font-semibold text-gray-800 mb-4">
              Phân bổ đơn hàng
            </h2>
            {chart.status.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie
                    data={chart.status}
                    cx="50%"
                    cy="45%"
                    innerRadius={55}
                    outerRadius={85}
                    paddingAngle={3}
                    dataKey="value"
                  >
                    {chart.status.map((_, index) => (
                      <Cell key={index} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 13 }} />
                  <Legend
                    verticalAlign="bottom"
                    iconType="circle"
                    iconSize={8}
                    formatter={(value) => <span className="text-xs text-gray-600">{value}</span>}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[280px] flex items-center justify-center">
                <p className="text-sm text-gray-400">Chưa có dữ liệu</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Orders Bar Chart */}
      {chart && (
        <div className="bg-white rounded-xl border border-gray-100 p-5">
          <h2 className="text-sm font-semibold text-gray-800 mb-4">
            Số lượng đơn hàng theo tháng
          </h2>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={chart.monthly}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#94a3b8" }} />
              <YAxis tick={{ fontSize: 12, fill: "#94a3b8" }} allowDecimals={false} />
              <Tooltip
                formatter={(value) => [Number(value), "Đơn hàng"]}
                contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 13 }}
              />
              <Bar dataKey="orders" fill="#3b82f6" radius={[6, 6, 0, 0]} barSize={40} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Recent Orders */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-50">
          <h2 className="text-sm font-semibold text-gray-800">Đơn hàng gần đây</h2>
        </div>
        {loading ? (
          <div className="p-5 space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-12 bg-gray-50 rounded-lg animate-pulse" />
            ))}
          </div>
        ) : stats?.recentOrders && stats.recentOrders.length > 0 ? (
          <div className="overflow-x-auto scrollbar-custom">
            <table className="w-full min-w-150">
              <thead>
                <tr className="bg-gray-50/50">
                  <th className="text-[11px] uppercase tracking-wider text-gray-400 font-semibold text-left px-5 py-3">Mã đơn</th>
                  <th className="text-[11px] uppercase tracking-wider text-gray-400 font-semibold text-left px-5 py-3">Khách hàng</th>
                  <th className="text-[11px] uppercase tracking-wider text-gray-400 font-semibold text-right px-5 py-3">Tổng tiền</th>
                  <th className="text-[11px] uppercase tracking-wider text-gray-400 font-semibold text-center px-5 py-3">Trạng thái</th>
                  <th className="text-[11px] uppercase tracking-wider text-gray-400 font-semibold text-right px-5 py-3">Thời gian</th>
                </tr>
              </thead>
              <tbody>
                {stats.recentOrders.map((order) => (
                  <tr key={order._id} className="border-t border-gray-50 hover:bg-gray-50/50 transition-colors">
                    <td className="px-5 py-3">
                      <span className="text-sm font-semibold text-blue-600">{order.orderCode}</span>
                    </td>
                    <td className="px-5 py-3 text-sm text-gray-700">{order.customer.name}</td>
                    <td className="px-5 py-3 text-sm font-medium text-gray-800 text-right">
                      {formatCurrency(order.totalAmount)}
                    </td>
                    <td className="px-5 py-3 text-center">
                      <span className={`inline-block px-2.5 py-1 text-xs font-medium rounded-full ${STATUS_STYLES[order.status]}`}>
                        {STATUS_LABELS[order.status]}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-xs text-gray-400 text-right">{formatDate(order.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="py-12 text-center">
            <FaShoppingCart className="text-3xl text-gray-200 mx-auto mb-2" />
            <p className="text-sm text-gray-400">Chưa có đơn hàng nào</p>
          </div>
        )}
      </div>
    </div>
  );
};
